/**
 * Dashboard Layout Component
 *
 * This component provides the main layout structure for all dashboard pages, including:
 * - Header with user information and actions
 * - Sidebar navigation menu
 * - System health alerts and notifications
 * - Content area for child components
 *
 * The layout also checks for system health issues and displays appropriate error banners.
 */

import styled from "styled-components";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useState } from "react";
import Sidebar from "../sidebar";
import Header from "../header/Header";
import {
  generateSystemIssueText,
  generateSystemActionText
} from "../../services/common-service";
import {
  getNonRejectedRawHookData,
  getLatestSystemHealthError
} from "../../services/api-declaration";
import { showError } from "../../services/toast-service";
import { AgentAssignmentProvider } from "../../context/agent-assignment-context";

/**
 * Styled component for displaying system error notifications at the top of the dashboard
 * Uses a prominent red background with white text to draw attention to critical issues
 */
const ErrorNotificationBanner = styled.div`
  background: red;
  padding: 2px;
  padding-left: 20px;
  color: white;
  font-size: 12px;
  font-weight: bold;
`;

/**
 * Main dashboard layout component that wraps all dashboard pages
 *
 * @param {React.ReactNode} children - The content to render within the layout
 * @returns {JSX.Element} The complete dashboard layout with header, sidebar and content
 */
const DashboardLayout = ({ children }) => {
  // Get authentication state from Redux
  const { user } = useSelector((state) => state.auth);

  // State for system health monitoring
  const [hasAnyNonRejectedRawData, setHasAnyNonRejectedRawData] =
    useState(false);
  const [systemHealthData, setSystemHealthData] = useState(null);

  /**
   * Fetches system health information and Facebook ad lead errors
   * Displays error banners for critical system issues
   */
  const fetchFbAdLeadError = async () => {
    try {
      // Check for system health errors
      const sysHealthRes = await getLatestSystemHealthError();
      setSystemHealthData(sysHealthRes.data.data[0]);

      // If no system health errors, check for unprocessed Facebook lead data
      if (!sysHealthRes.data.data.length) {
        try {
          const nonRejectedFbDataRes = await getNonRejectedRawHookData();
          if (nonRejectedFbDataRes.data.data.length) {
            setHasAnyNonRejectedRawData(true);
          }
        } catch (error) {
          console.log("Error in fetch fb hook raw data", error);
          showError("Error in fetch fb hook raw data!");
        }
      }
    } catch (error) {
      console.log("Error in fetch system health alert", error);
      showError("Error in fetch system health alert!");
    }
  };

  // Use callback to prevent effect dependencies issues
  const fbAdLeadErrorCb = useCallback(fetchFbAdLeadError, []);

  // Check system health on component mount
  useEffect(() => {
    fbAdLeadErrorCb();
  }, [fbAdLeadErrorCb]);

  /**
   * Render the dashboard layout with:
   * - Error notification banners (if any system issues exist)
   * - Header component with user info and actions
   * - Sidebar navigation
   * - Main content area for page components
   */
  return (
    <>
      {/* Facebook Token Error Banner */}
      {user.workspace.noValidPrimaryTokenExists && (
        <ErrorNotificationBanner>
          Primary Facebook Access Token is not valid anymore need action!
        </ErrorNotificationBanner>
      )}

      {/* System Health Error Banner */}
      {systemHealthData && (
        <ErrorNotificationBanner>
          Alert! {generateSystemIssueText(systemHealthData.issue)} error
          occurred in {generateSystemActionText(systemHealthData.action)}.
        </ErrorNotificationBanner>
      )}

      {/* Unprocessed Facebook Lead Data Banner */}
      {hasAnyNonRejectedRawData && (
        <ErrorNotificationBanner>
          Alert! There are some non created facebook ad leads in raw hook data.
        </ErrorNotificationBanner>
      )}

      <AgentAssignmentProvider>
        {/* Header Component */}
        <Header />

        {/* Main Layout Structure */}
        <div className="d-flex bg-white">
          {/* Sidebar Navigation */}
          <Sidebar />
          {/* Main Content Area */}
          <div className="main-content">
            <div className="container-fluid h-100">
              <div className="row h-100">
                <div className="col-sm-12 px-3">
                  {/* Page Content */}
                  {children}
                </div>
              </div>
            </div>
          </div>
        </div>
      </AgentAssignmentProvider>
    </>
  );
};

export default DashboardLayout;
